<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import type { FormKitFile } from '@formkit/inputs'
import { humanFileSize } from '~/lib'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  name: string
  required: boolean
  multiple?: boolean
  label?: string
  placeholder?: string
  validation?: string
  validationMessages?: {
    [key: string]: string
  }
  accept?: string
  help?: string
  customUploader?: boolean
  id?: string
}>()

const validationString = computed(() => {
  const validationString = props.validation ?? ''
  const v = validationString.split('|')
  if (props.required) {
    v.push('required')
  }

  return v.filter(Boolean).join('|')
})

interface UploadedFile {
  id: string
  name: string
  url: string
}

const uploadedFiles = ref<UploadedFile[]>([])

const isFormKitFile = (file: unknown): file is FormKitFile =>
  typeof file === 'object' && file !== null && 'file' in file && 'name' in file

const getFormKitFileName = (file: unknown) => (isFormKitFile(file) ? file.name : '')
const getFormKitFileSize = (file: unknown) => (isFormKitFile(file) && file.file ? humanFileSize(file.file.size) : '')

const handleFileUpload = (data?: unknown) => {
  if (!Array.isArray(data)) return
  const files = data.filter(isFormKitFile)
  if (!props.customUploader) return

  uploadedFiles.value.splice(0, uploadedFiles.value.length)

  files.forEach(async (item) => {
    if (!item.file) return

    const body = new FormData()
    body.append(props.name, item.file)

    const res = await fetch('/api/tocco/upload-file', {
      method: 'POST',
      body: body,
    })

    if (res.ok) {
      const data = await res.json()
      uploadedFiles.value.push({
        id: data.id,
        name: data.name,
        url: data.url,
      })
    } else {
      console.log('Error uploading file')
    }
  })
}
</script>

<template>
<FormKitLazyProvider config-file="true">

  <FormKit
    :id="id"
    type="file"
    :name="name"
    :label="label ?? ''"
    :validation="validationString ?? ''"
    :validation-messages="validationMessages ?? {}"
    :accept="accept ?? ''"
    :multiple="multiple ?? 'false'"
    :classes="{
      outer: `custom-file-input ${$attrs.class ?? ''}`,
    }"
    :help="help ?? ''"
    @input="(e: unknown) => handleFileUpload(e)"
  >
    <template #label="context">
      <span class="formkit-label">
        {{ label }}
      </span>
      <label :for="context.id">
        <div class="custom-upload-button">
          <span class="icon">
            <Icon name="upload" />
          </span>
          <span class="label">{{ placeholder ?? t('upload.file') }}</span>
        </div>
      </label>
    </template>

    <template #fileName="context">
      <li class="file-wrapper">
        <div class="icon">
          <Icon name="document" />
        </div>

        <div class="file-info">
          <h5 class="file-name">
            {{ getFormKitFileName(context.file) }}
          </h5>

          <span v-if="getFormKitFileSize(context.file)" class="file-size">
            {{ getFormKitFileSize(context.file) }}
          </span>
        </div>
      </li>
    </template>

    <template #fileRemoveIcon>
      <div class="delete-icon">
        <Icon name="delete" />
      </div>
    </template>
  </FormKit>

  <FormKit v-if="uploadedFiles" type="group" name="uploadIds">
    <FormKit
      v-for="(file, index) in uploadedFiles"
      :id="`${id || name}-upload-${index}`"
      :key="`item-${file.id}`"
      type="hidden"
      :name="`${name}-${index}`"
      :value="file.id"
    />
  </FormKit>

</FormKitLazyProvider>
</template>

<style lang="scss" scoped></style>
